import { FC } from "react";
import { Drawer, Spin, Tag, Empty } from "antd";
import {
  MapPin,
  Calendar,
  Truck,
  User,
  Package,
  Weight,
  Box,
  X,
  ChevronRight,
} from "lucide-react";
import dayjs from "dayjs";
import { useGetShuttleById } from "~/services/vessel";
import { useShuttleStore } from "../store/shuttle.store";
import { StatusBadge } from "../status-badge";
import { VesselStaffRole, InventoryType } from "~/utils/constants";
import { formatStaffName } from "~/utils/format-staff-name";
import { TInventory } from "~/types";

const formatDateWithDay = (date: string | Date) => {
  const d = dayjs(date);
  if (!d.isValid()) return "—";
  return d.format("ddd, MMM D");
};

export const ShuttleDetailPanel: FC = () => {
  const { detailState, closeShuttleDetail, inventoryDrawerOpen, openInventoryDrawer, closeInventoryDrawer } = useShuttleStore();
  const { data: shuttle, isLoading } = useGetShuttleById(
    detailState.shuttleId
  );

  if (!detailState.open) return null;

  const getDriversForVessel = (vesselStaffs: any[]) =>
    vesselStaffs
      ?.filter((vs) => vs.roles.includes(VesselStaffRole.DRIVER))
      ?.map((vs) => formatStaffName(vs.staff)) || [];

  if (isLoading) {
    return (
      <div className="bg-white rounded-md border border-[#e5e5e5] p-8 flex justify-center">
        <Spin />
      </div>
    );
  }

  if (!shuttle) {
    return (
      <div className="bg-white rounded-md border border-[#e5e5e5] p-6">
        <Empty description="Shuttle not found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  // Service tickets tagged to this shuttle via shuttle_id FK
  const allTickets = shuttle.serviceTickets || [];

  // Deduplicate inventories by ID across all tickets
  const invMap = new Map<string, typeof allTickets[0]["inventories"] extends (infer U)[] | undefined ? NonNullable<U> : never>();
  allTickets.forEach((st) =>
    (st.inventories || []).forEach((inv) => {
      if (!invMap.has(inv.id)) invMap.set(inv.id, inv);
    })
  );
  const allInventories = Array.from(invMap.values());
  const totalWeight = allInventories.reduce((s, inv) => s + (Number(inv.weight) || 0), 0);
  const usedVolume = allInventories.reduce((s, inv) => {
    const l = Number(inv.length) || 0;
    const w = Number(inv.width) || 0;
    const h = Number(inv.height) || 1; // default 1" depth for flat/2D items
    return s + (l * w * h) / 1728;
  }, 0);
  const totalPieces = allInventories.length;

  // Total vessel capacity
  const totalCapacity = (shuttle.vessels || []).reduce((s, v) => {
    const l = v.boxLength || 0;
    const w = v.doorWidth || 0;
    const h = v.internalHeight || 0;
    return s + (l * w * h) / 1728;
  }, 0);

  const usedPct = totalCapacity > 0 ? Math.min((usedVolume / totalCapacity) * 100, 100) : 0;
  const remaining = Math.max(totalCapacity - usedVolume, 0);

  // Group tickets by booking
  const bookingMap = new Map<string, {
    referenceNumber: string;
    clientName: string;
    serviceTypes: string[];
    pieceCount: number;
    totalWeight: number;
    totalVolume: number;
  }>();

  allTickets.forEach((st) => {
    const booking = st.jobTicket;
    if (!booking) return;
    const key = booking.id || st.id;
    if (!bookingMap.has(key)) {
      bookingMap.set(key, {
        referenceNumber: booking.referenceNumber || "—",
        clientName: st.client?.companyName || booking.client?.companyName || "—",
        serviceTypes: [],
        pieceCount: 0,
        totalWeight: 0,
        totalVolume: 0,
      });
    }
    const entry = bookingMap.get(key)!;
    entry.serviceTypes.push(st.serial);
    const inv = st.inventories || [];
    entry.pieceCount += inv.length;
    entry.totalWeight += inv.reduce((s, i) => s + (Number(i.weight) || 0), 0);
    entry.totalVolume += inv.reduce((s, i) => {
      const l = Number(i.length) || 0;
      const w = Number(i.width) || 0;
      const h = Number(i.height) || 1;
      return s + (l * w * h) / 1728;
    }, 0);
  });

  const bookings = Array.from(bookingMap.values());

  const barColor =
    usedPct >= 90 ? "#D9534F" : usedPct >= 70 ? "#F0AD4E" : "#1a1a2e";

  return (
    <div className="bg-white rounded-md border border-[#e5e5e5] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e5e5]">
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-semibold text-[#111]">
            {shuttle.shuttleRef || "Shuttle"}
          </span>
          {shuttle.status && <StatusBadge status={shuttle.status} />}
        </div>
        <button
          onClick={closeShuttleDetail}
          className="text-[#999] hover:text-[#333] transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Route & Dates + Metrics */}
        <div className="flex gap-6">
          <div className="flex-1 grid grid-cols-2 gap-x-6 gap-y-2">
            <InfoRow icon={<MapPin size={13} />} label="Origin" value={shuttle.origin || "—"} />
            <InfoRow icon={<MapPin size={13} />} label="Destination" value={shuttle.destination || "—"} />
            <InfoRow icon={<Calendar size={13} />} label="Load" value={formatDateWithDay(shuttle.loadDate)} />
            <InfoRow icon={<Calendar size={13} />} label="Departure" value={formatDateWithDay(shuttle.departureDate)} />
            <InfoRow icon={<Calendar size={13} />} label="Arrival" value={formatDateWithDay(shuttle.arrivalDate)} />
            <InfoRow icon={<Calendar size={13} />} label="Return" value={formatDateWithDay(shuttle.returnDate)} />
          </div>

          {/* Metrics */}
          <div className="flex gap-px rounded-md overflow-hidden border border-[#e5e5e5] self-start">
            <button
              onClick={allInventories.length > 0 ? openInventoryDrawer : undefined}
              className={`flex flex-col items-center px-4 py-2.5 min-w-[72px] transition-colors ${
                allInventories.length > 0 ? "hover:bg-[#f5f5f5] cursor-pointer" : ""
              }`}
            >
              <span className="text-[#888] mb-0.5"><Package size={14} /></span>
              <div className="flex items-center gap-1">
                <span className="text-[15px] font-semibold text-[#111]">{totalPieces}</span>
                {allInventories.length > 0 && <ChevronRight size={12} className="text-[#999]" />}
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#999]">Pieces</span>
            </button>
            <MetricCell icon={<Box size={14} />} value={usedVolume.toFixed(1)} label="ft³ used" border />
            <MetricCell icon={<Weight size={14} />} value={totalWeight.toFixed(1)} label="lbs" border />
          </div>
        </div>

        {/* Capacity Bar */}
        {totalCapacity > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <SectionLabel text="Shuttle Capacity" />
              <span className="text-[12px] text-[#555]">
                {usedVolume.toFixed(1)} / {totalCapacity.toFixed(1)} ft³
                <span className="text-[#999] ml-1">
                  ({remaining.toFixed(1)} ft³ available)
                </span>
              </span>
            </div>
            <div className="h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usedPct}%`,
                  backgroundColor: barColor,
                }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-[#999]">0 ft³</span>
              <span className="text-[10px] font-medium" style={{ color: barColor }}>
                {usedPct.toFixed(0)}% filled
              </span>
              <span className="text-[10px] text-[#999]">{totalCapacity.toFixed(1)} ft³</span>
            </div>
          </div>
        )}

        {/* Vessels & Drivers */}
        <div>
          <SectionLabel text="Vessels & Drivers" />
          <div className="space-y-2 mt-2">
            {shuttle.vessels?.length ? (
              shuttle.vessels.map((vessel) => {
                const drivers = getDriversForVessel(vessel.vesselStaffs || []);
                const vesselVol =
                  ((vessel.boxLength || 0) * (vessel.doorWidth || 0) * (vessel.internalHeight || 0)) / 1728;
                return (
                  <div
                    key={vessel.id}
                    className="bg-[#fafafa] border border-[#e5e5e5] rounded px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck size={13} className="text-[#888]" />
                        <span className="text-[13px] font-semibold text-[#111]">
                          {vessel.vesselRef || vessel.name}
                        </span>
                        {vessel.vesselType && (
                          <span className="text-[11px] text-[#999] bg-white border border-[#e5e5e5] rounded px-1.5 py-0.5">
                            {vessel.vesselType.name}
                          </span>
                        )}
                      </div>
                      {drivers.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <User size={12} className="text-[#888]" />
                          <span className="text-[12px] text-[#555]">
                            {drivers.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-[11px] text-[#888]">
                      {vessel.licensePlate && (
                        <span>Plate: <span className="text-[#555] font-medium">{vessel.licensePlate}</span></span>
                      )}
                      {vessel.boxLength && vessel.doorWidth && vessel.internalHeight && (
                        <span>
                          Interior: <span className="text-[#555] font-medium">
                            {vessel.boxLength}″ × {vessel.doorWidth}″ × {vessel.internalHeight}″
                          </span>
                        </span>
                      )}
                      {vesselVol > 0 && (
                        <span>
                          Capacity: <span className="text-[#555] font-medium">{vesselVol.toFixed(1)} ft³</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="text-[12px] text-[#999]">No vessels assigned</span>
            )}
          </div>
        </div>

        {/* Linked Bookings */}
        {bookings.length > 0 && (
          <div>
            <SectionLabel text="Linked Bookings" />
            <div className="mt-2 border border-[#e5e5e5] rounded-md overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_auto_80px_80px] text-[11px] font-medium uppercase tracking-wider text-[#999] bg-[#fafafa] px-3 py-2 border-b border-[#e5e5e5]">
                <span>Booking</span>
                <span>Client</span>
                <span>Services</span>
                <span className="text-right">Volume</span>
                <span className="text-right">Weight</span>
              </div>
              {bookings.map((b, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-[1fr_1fr_auto_80px_80px] items-center px-3 py-2 text-[13px] ${
                    i < bookings.length - 1 ? "border-b border-[#f0f0f0]" : ""
                  }`}
                >
                  <span className="font-medium text-[#111]">{b.referenceNumber}</span>
                  <span className="text-[#555]">{b.clientName}</span>
                  <div className="flex gap-1">
                    {b.serviceTypes.map((s) => (
                      <Tag key={s} className="text-[11px] m-0 leading-tight" style={{ padding: "1px 6px" }}>
                        {s}
                      </Tag>
                    ))}
                  </div>
                  <span className="text-right text-[#333]">{b.totalVolume.toFixed(1)} ft³</span>
                  <span className="text-right text-[#333]">{b.totalWeight.toFixed(1)} lbs</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {allTickets.length === 0 && (
          <div className="text-[12px] text-[#999] text-center py-3">
            No service tickets linked to this shuttle
          </div>
        )}
      </div>

      {/* Inventory Drawer */}
      <InventoryDrawer
        open={inventoryDrawerOpen}
        onClose={closeInventoryDrawer}
        inventories={allInventories}
        shuttleRef={shuttle.shuttleRef}
      />
    </div>
  );
};

const InfoRow: FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-2">
    <span className="text-[#999]">{icon}</span>
    <span className="text-[11px] font-medium uppercase tracking-wider text-[#999] w-[72px]">
      {label}
    </span>
    <span className="text-[13px] text-[#333] font-medium">{value}</span>
  </div>
);

const MetricCell: FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  border?: boolean;
}> = ({ icon, value, label, border }) => (
  <div
    className={`flex flex-col items-center px-4 py-2.5 min-w-[72px] ${
      border ? "border-l border-[#e5e5e5]" : ""
    }`}
  >
    <span className="text-[#888] mb-0.5">{icon}</span>
    <span className="text-[15px] font-semibold text-[#111]">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-[#999]">{label}</span>
  </div>
);

const SectionLabel: FC<{ text: string }> = ({ text }) => (
  <span className="text-[11px] font-medium uppercase tracking-[1.2px] text-[#999]">
    {text}
  </span>
);

const formatDims = (inv: TInventory) => {
  const parts: string[] = [];
  if (inv.length) parts.push(`${inv.length}`);
  if (inv.width) parts.push(`${inv.width}`);
  if (inv.height) parts.push(`${inv.height}`);
  return parts.length > 0 ? `${parts.join(" × ")}″` : "—";
};

const itemTypeLabel = (type: string) => {
  switch (type) {
    case InventoryType.PACKAGE:
      return "Pkg";
    case InventoryType.PART:
      return "Part";
    default:
      return "Pc";
  }
};

const InventoryDrawer: FC<{
  open: boolean;
  onClose: () => void;
  inventories: TInventory[];
  shuttleRef: string | null;
}> = ({ open, onClose, inventories, shuttleRef }) => {
  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-semibold text-[#111]">
            Inventory — {shuttleRef || "Shuttle"}
          </span>
          <span className="text-[12px] text-[#999] font-normal">
            {inventories.length} item{inventories.length !== 1 ? "s" : ""}
          </span>
        </div>
      }
      open={open}
      onClose={onClose}
      width={640}
      styles={{ body: { padding: 0 } }}
    >
      {inventories.length === 0 ? (
        <div className="p-6">
          <Empty description="No inventory on this shuttle" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <div>
          {inventories.map((inv, i) => (
            <div
              key={inv.id}
              className={`px-5 py-3 ${i < inventories.length - 1 ? "border-b border-[#f0f0f0]" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: inv.itemType === InventoryType.PACKAGE ? "#EDE9FE" : "#E0F2FE",
                        color: inv.itemType === InventoryType.PACKAGE ? "#7C3AED" : "#0369A1",
                      }}
                    >
                      {itemTypeLabel(inv.itemType)}
                    </span>
                    <span className="text-[13px] font-semibold text-[#111] truncate">
                      {inv.title || "Untitled"}
                    </span>
                  </div>
                  {inv.artistName && (
                    <div className="text-[12px] text-[#555] mt-0.5">{inv.artistName}</div>
                  )}
                </div>
                {inv.inventoryRef && (
                  <span className="text-[11px] text-[#999] font-mono whitespace-nowrap">
                    {inv.inventoryRef}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[11px] text-[#888]">
                <span>
                  Dims: <span className="text-[#333] font-medium">{formatDims(inv)}</span>
                </span>
                {inv.weight != null && Number(inv.weight) > 0 && (
                  <span>
                    Weight: <span className="text-[#333] font-medium">{inv.weight} lbs</span>
                  </span>
                )}
                {inv.medium && (
                  <span>
                    Medium: <span className="text-[#333] font-medium">{inv.medium}</span>
                  </span>
                )}
                {inv.client?.companyName && (
                  <span>
                    Client: <span className="text-[#333] font-medium">{inv.client.companyName}</span>
                  </span>
                )}
                {inv.clientInternalRef && (
                  <span>
                    Ref: <span className="text-[#333] font-medium">{inv.clientInternalRef}</span>
                  </span>
                )}
                {inv.packingType && (
                  <span>
                    Packing: <span className="text-[#333] font-medium">{inv.packingType}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};
